import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@components/forms/Form';
import { FormSection } from '@components/forms';
import { Alert, Box, Typography, Grid } from '@mui/material';
import { Button } from '@components/button/Button';
import { useNavigate } from 'react-router-dom';
import { getFormFields, getPartnerFields } from '@pages/Login/SignUpView/formFields';
import { useCreateUser } from '@services/user/useCreateUser';
import { enqueueSnackbar } from 'notistack';
import { ToastType } from '@components/toast';
import { useToastStore } from '@store/Toast';
import { LoginSource, RegistrationRole } from '@pages/Login/constants';
import { IndicationSummaryGetResponse } from '@lio/api-types';
import { SignUpFormValues } from '@pages/Login/SignUpView/SignUpFormValues.type';
import { RecaptchaEnterpriseProvider } from '@components/recaptcha/RecaptchaEnterpriseProvider';
import { getRecaptchaConfig, getAppEnvironment, Environment } from '@/config';
import { GoogleRecaptcha } from '@components/recaptcha';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { RegistrationContactFooter } from '@pages/Login/SignUpView/RegistrationContactFooter';
import { BrokerTermsAcceptanceStep } from '@pages/Login/SignUpView/BrokerTermsAcceptanceStep';
import { RegistrationFormFooter } from '@pages/Login/SignUpView/RegistrationFormFooter';
import { BrokerPartnerIdSection } from '@pages/Login/SignUpView/BrokerPartnerIdSection';
import { LegacyPartnerAffiliationSection } from '@pages/Login/SignUpView/LegacyPartnerAffiliationSection';
import { signUpContainerSx } from '@pages/Login/SignUpView/SignUpView.styles';
import { getTitleForRole } from '@pages/Login/SignUpView/registrationContent';

export const SignUpView = ({
  loginSource,
  indicationSummary,
  registrationRole,
  onSelectDifferentRole,
}: {
  loginSource: LoginSource;
  indicationSummary?: IndicationSummaryGetResponse;
  registrationRole?: RegistrationRole;
  onSelectDifferentRole?: () => void;
}) => {
  const navigate = useNavigate();
  const { enablePersistToast } = useToastStore();
  const { createUser, isPending: isCreateUserLoading } = useCreateUser();
  const [partnerFieldChecked, setPartnerFieldChecked] = React.useState(!!indicationSummary);
  const [brokerStep, setBrokerStep] = React.useState<1 | 2>(1);

  const recaptchaConfig = getRecaptchaConfig();
  const currentEnv = getAppEnvironment();
  const isDevOrInt =
    currentEnv === Environment.Dev || currentEnv === Environment.Int || currentEnv === Environment.Local;
  const [recaptchaToken, setRecaptchaToken] = React.useState<string>('');
  const [showRecaptcha, setShowRecaptcha] = React.useState(false);
  const [isGeneratingToken, setIsGeneratingToken] = React.useState(false);

  const methods = useForm<SignUpFormValues>({
    mode: 'onChange',
    defaultValues: {
      email: indicationSummary?.createdByUser?.email ?? '',
      partnerId: indicationSummary?.brokerage?.osPartnerCode ?? '',
    },
  });

  useEffect(() => {
    methods.reset({
      email: indicationSummary?.createdByUser?.email ?? '',
      partnerId: indicationSummary?.brokerage?.osPartnerCode ?? '',
    });

    setPartnerFieldChecked(!!indicationSummary);
    setRecaptchaToken('');
    setShowRecaptcha(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicationSummary]);

  // Clean up reCAPTCHA script when component unmounts
  useEffect(() => {
    return () => {
      const recaptchaScripts = document.querySelectorAll('script[src*="recaptcha"]');
      recaptchaScripts.forEach((script) => script.remove());

      const recaptchaContainers = document.querySelectorAll('[id*="recaptcha"], [class*="recaptcha"]');
      recaptchaContainers.forEach((container) => container.remove());

      if (window.grecaptcha) {
        delete (window as any).grecaptcha;
      }
    };
  }, []);

  const lockForm = !!indicationSummary;

  const formFields = getFormFields({ loginSource: lockForm ? loginSource : undefined });
  const partnerFields = getPartnerFields({ indicationSummary: lockForm ? indicationSummary : undefined });

  const handleCreateAccount = async () => {
    if (!recaptchaToken) {
      setShowRecaptcha(true);
      setIsGeneratingToken(true);
      return;
    }

    const formData = methods.getValues();

    let isCustomerUser: boolean;
    if (registrationRole === RegistrationRole.POLICYHOLDER || registrationRole === RegistrationRole.BILLING_CONTACT) {
      isCustomerUser = true;
    } else if (registrationRole === RegistrationRole.BROKER_PRODUCER) {
      isCustomerUser = false;
    } else {
      isCustomerUser = !formData.partnerId && !indicationSummary;
    }

    const isBrokerProducerRegistration =
      registrationRole === RegistrationRole.BROKER_PRODUCER || (!registrationRole && !isCustomerUser);
    const osPartnerCode = isBrokerProducerRegistration
      ? formData.partnerId || indicationSummary?.brokerage?.osPartnerCode
      : undefined;

    const userCreateBody = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      isCustomerUser,
      registration: {
        password: formData.password,
        partnerId: osPartnerCode ?? undefined,
        recaptchaToken: recaptchaToken,
      },
    };

    try {
      await createUser(userCreateBody, {
        onError: async (error) => {
          const messageToDisplay = await getApiErrorMessage(
            error,
            'Failed to register user. Please contact support at 1-877-LIO-7155 (1-877-546-7155) for assistance.',
          );

          enqueueSnackbar(messageToDisplay, {
            variant: ToastType.Error,
            persist: true,
          });
        },
        onSuccess: () => {
          enablePersistToast();

          enqueueSnackbar('Account successfully created', {
            variant: ToastType.Success,
            persist: true,
          });

          // Navigate to /login with justRegistered state so Login.tsx knows not to
          // redirect back to /sign-up while the Okta webhook is still setting idpUserId
          navigate('/login', { state: { justRegistered: true } });
        },
      });
    } catch {
      // onError callback handles UI feedback
    }
  };

  const handleContinueToBrokerStep2 = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      setBrokerStep(2);
    }
  };

  const handleBackToSignIn = () => {
    navigate('/login');
  };

  const isValid = methods.formState.isValid && Object.keys(methods.formState.errors).length === 0;

  const handleUncheckPartnerField = () => {
    setPartnerFieldChecked(!partnerFieldChecked);
    methods.trigger();
  };

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
    setShowRecaptcha(false);
    setIsGeneratingToken(false);

    setTimeout(() => {
      if (token) {
        handleCreateAccount();
      }
    }, 100);
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken('');
    setShowRecaptcha(false);
    setIsGeneratingToken(false);
    enqueueSnackbar('reCAPTCHA verification failed. Please try again.', {
      variant: ToastType.Error,
    });
  };

  const title = getTitleForRole(registrationRole);

  const recaptchaWidget = showRecaptcha ? (
    <RecaptchaEnterpriseProvider siteKey={recaptchaConfig.siteKey}>
      <GoogleRecaptcha onVerify={handleRecaptchaVerify} onError={handleRecaptchaError} action='registration' />
    </RecaptchaEnterpriseProvider>
  ) : null;

  if (registrationRole === RegistrationRole.BROKER_PRODUCER && brokerStep === 2) {
    return (
      <BrokerTermsAcceptanceStep
        title={title}
        recaptchaWidget={recaptchaWidget}
        isCreateUserLoading={isCreateUserLoading}
        isGeneratingToken={isGeneratingToken}
        loginSource={loginSource}
        onCreateAccount={handleCreateAccount}
        onBackToSignIn={handleBackToSignIn}
        onSelectDifferentRole={onSelectDifferentRole}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Grid
        container
        sx={[
          {
            textAlign: 'center',
          },
          signUpContainerSx,
        ]}
      >
        <Grid size={{ xs: 12 }}>
          <Typography variant='h5'>{title}</Typography>
        </Grid>
        <Grid>
          <Form methods={methods} formId='sign-up-form'>
            <Grid
              container
              spacing={0}
              sx={{
                mb: 3,
                padding: '0px 16px 0 16px',
              }}
            >
              <FormSection fields={formFields} styleConfig={{ spacing: 2 }} />

              {registrationRole === RegistrationRole.BROKER_PRODUCER && (
                <BrokerPartnerIdSection partnerFields={partnerFields} />
              )}

              {loginSource !== LoginSource.QUOTE_SETUP_PRESET && !registrationRole && (
                <LegacyPartnerAffiliationSection
                  partnerFieldChecked={partnerFieldChecked}
                  lockForm={lockForm}
                  partnerFields={partnerFields}
                  onPartnerFieldToggle={handleUncheckPartnerField}
                />
              )}
              {/*  // FUTURE
              {lockForm && (
                <Typography variant='bodySmall'>
                  If the information above is wrong, please
                  <Button
                    color='secondary'
                    variant='text'
                    disableRipple
                    onClick={() => {
                      handleClearForm();
                    }}
                    sx={{
                      fontSize: 'inherit',
                      margin: 0,
                      padding: 0,
                      ml: '4px',
                      mt: '-2px',
                      display: 'inline',
                    }}
                  >
                    register here
                  </Button>
                </Typography>
              )} */}

              {recaptchaWidget && <Grid size={{ xs: 12 }}>{recaptchaWidget}</Grid>}

              {isDevOrInt && (
                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Alert severity='error'>Please use the QA env to create accounts.</Alert>
                </Grid>
              )}

              <Grid
                size={{ xs: 12, sm: 'auto' }}
                sx={{
                  margin: 'auto',
                  mt: 2,
                }}
              >
                {registrationRole === RegistrationRole.BROKER_PRODUCER ? (
                  <Button
                    variant='contained'
                    fullWidth
                    disabled={!isValid || isDevOrInt}
                    onClick={handleContinueToBrokerStep2}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    fullWidth
                    isLoading={isCreateUserLoading || isGeneratingToken}
                    disabled={!isValid || isDevOrInt}
                    onClick={handleCreateAccount}
                  >
                    {isGeneratingToken ? 'Verifying...' : 'Create Account'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </Grid>

        <RegistrationFormFooter
          loginSource={loginSource}
          onBackToSignIn={handleBackToSignIn}
          onSelectDifferentRole={onSelectDifferentRole}
        />
      </Grid>
      {registrationRole && <RegistrationContactFooter />}
    </Box>
  );
};
